import { apiFetch } from "../helper/apiClient";

function getApiError(body, fallback) {
  return typeof body.detail === "string" ? body.detail : fallback;
}

export async function searchCardProducts(query) {
  const res = await apiFetch(
    `products/admin/products/search/?q=${encodeURIComponent(query)}`,
    { method: "GET" },
    { auth: true }
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(getApiError(body, "Failed to search products"));
  }
  return body;
}

export async function lookupCardByBarcode(barcode) {
  const res = await apiFetch(`products/admin/card/${encodeURIComponent(barcode)}/`, {
    method: "GET",
  }, { auth: true });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = body.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d.message || d).join(", ")
          : res.status === 401
            ? "Not logged in — log out and back in"
            : res.status === 403
              ? "No permission — account needs is_test or is_staff"
              : "Card not found";
    throw new Error(msg);
  }
  return body;
}

export async function removeCard(cardId) {
  const res = await apiFetch(
    `products/admin/card/${encodeURIComponent(cardId)}/remove/`,
    { method: "DELETE" },
    { auth: true }
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(getApiError(body, "Failed to remove card"));
  }
  return body;
}

export async function removeCards(cardIds) {
  const res = await apiFetch(
    "products/admin/cards/remove/",
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ card_ids: cardIds }),
    },
    { auth: true }
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(getApiError(body, "Failed to remove chosen cards"));
  }
  return body;
}

export async function receiveStock({ productId, condition, variant, priceCents, quantity, reason }) {
  const res = await apiFetch("products/admin/receive/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      product_id: productId,
      condition,
      variant,
      price_cents: priceCents,
      quantity,
      reason,
    }),
  }, { auth: true });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.detail || "Failed to receive stock");
  }
  return body;
}

export async function getAdminTransactions({ page = 1, pageSize = 25 } = {}) {
  const params = new URLSearchParams({
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await apiFetch(
    `products/admin/transactions/?${params.toString()}`,
    { method: "GET" },
    { auth: true }
  );

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const detail = getApiError(body, "");
    throw new Error(
      detail ||
        (res.status === 404
          ? "Transactions endpoint not found — restart the backend server"
          : res.status === 401 || res.status === 403
            ? "Not authorized to view transactions"
            : `Failed to load transactions (${res.status})`)
    );
  }
  return body;
}

export async function previewTaxReceipt(transactionId) {
  const params = new URLSearchParams();
  if (transactionId) params.set("transaction_id", String(transactionId));
  const query = params.toString();
  const res = await apiFetch(
    `products/admin/tax-receipts/${query ? `?${query}` : ""}`,
    { method: "GET" },
    { auth: true }
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(getApiError(body, "Failed to load tax invoice"));
  }
  return body;
}

export async function sendTaxReceipt({ email, customerName, transactionId, items }) {
  const payload = {
    email,
    customer_name: customerName || "",
    items: items || [],
  };
  if (transactionId) payload.transaction_id = Number(transactionId);

  const res = await apiFetch(
    "products/admin/tax-receipts/",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    { auth: true }
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(getApiError(body, "Failed to send tax invoice"));
  }
  return body;
}

export async function updateCardPrice({ productId, condition, variant, priceCents }) {
  const res = await apiFetch("products/admin/price/", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      product_id: productId,
      condition,
      variant,
      price_cents: priceCents,
    }),
  }, { auth: true });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body.detail || "Failed to update price");
  }
  return body;
}
